'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface BreadcrumbItem {
  label: string;
  path: string;
}

const ROUTE_LABELS: Record<string, string> = {
  'access-control-listings-management-dashboard': 'Dashboard',
  'comprehensive-listing-editor-panel': 'Editor',
  'profile-search-user-management-interface': 'Users',
};

interface BreadcrumbProps {
  customItems?: BreadcrumbItem[];
}

const Breadcrumb = ({ customItems }: BreadcrumbProps) => {
  const pathname = usePathname();

  const items: BreadcrumbItem[] = customItems ?? (() => {
    const segments = pathname.split('/').filter(Boolean);
    const crumbs: BreadcrumbItem[] = [{ label: 'Home', path: '/access-control-listings-management-dashboard' }];
    let accumulated = '';
    segments.forEach((seg) => {
      accumulated += `/${seg}`;
      const label = ROUTE_LABELS[seg] ?? seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      crumbs.push({ label, path: accumulated });
    });
    return crumbs;
  })();

  if (items.length <= 1) return null;

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1 flex-wrap">
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        return (
          <span key={item.path} className="flex items-center gap-1">
            {index > 0 && (
              <Icon
                name="ChevronRightIcon"
                size={14}
                variant="outline"
                className="text-muted-foreground shrink-0"
              />
            )}
            {isLast ? (
              <span
                className="font-caption text-sm font-medium text-foreground"
                aria-current="page"
              >
                {item.label}
              </span>
            ) : (
              <Link
                href={item.path}
                className="font-caption text-sm text-muted-foreground hover:text-primary transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 rounded"
              >
                {item.label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
};

export default Breadcrumb;